import { Link, useLocation } from "react-router-dom";
import { Play, Pause, SkipBack, SkipForward, Music2 } from "lucide-react";
import { Cover, mmss } from "./SongCard";
import { useMusicPlayer } from "../../context/MusicPlayerContext";

// Rendered once at App level. Full bar on /music pages; a small pill elsewhere so the set keeps playing but stays out of the way.
export function GlobalMusicBar() {
  const q = useMusicPlayer();
  const { pathname } = useLocation();
  const song = q.current;
  if (!song) return null;
  const onMusic = pathname.startsWith("/music");
  if (!onMusic) {
    return (
      <div className="fixed bottom-4 left-4 z-40 flex items-center gap-2 rounded-full bg-[#0a0608]/90 text-[#f3e9d2] backdrop-blur-xl border border-[#e9c46a]/40 pl-3 pr-1 py-1 shadow-xl" data-testid="mini-player">
        <Music2 size={14} className="text-[#e9c46a]" />
        <Link to={`/music/${song.slug || song.id}`} className="text-xs font-semibold max-w-[160px] truncate hover:text-[#e9c46a]" data-testid="mini-player-title">{song.title}</Link>
        <button type="button" onClick={() => q.toggle(q.index)} aria-label={q.playing ? "Pause" : "Play"} data-testid="mini-player-toggle" className="h-8 w-8 rounded-full bg-[#e9c46a] text-[#120a0c] grid place-items-center">{q.playing ? <Pause size={14} /> : <Play size={14} className="ml-0.5" />}</button>
      </div>
    );
  }
  const total = q.songs?.length || 0;
  return (
    <div className="fixed inset-x-0 bottom-0 z-40 border-t border-[#e9c46a]/30 bg-[#0a0608]/90 backdrop-blur-xl text-[#f3e9d2]" data-testid="now-playing-bar">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-3 flex items-center gap-4">
        <Link to={`/music/${song.slug || song.id}`} className="shrink-0"><Cover song={song} compact className="h-12 w-12 rounded-sm" /></Link>
        <div className="min-w-0 flex-1">
          <Link to={`/music/${song.slug || song.id}`} className="font-display text-lg leading-none truncate block hover:text-[#e9c46a]" data-testid="now-playing-title">{song.title}</Link>
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#f3e9d2]/55 mt-1">Track {String(q.index + 1).padStart(2, "0")} of {total} · {mmss(Math.floor(q.time))}{song.duration_sec ? ` / ${mmss(song.duration_sec)}` : ""}</p>
        </div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={q.prev} aria-label="Previous" data-testid="queue-prev" className="p-2 text-[#f3e9d2]/70 hover:text-[#e9c46a]"><SkipBack size={18} /></button>
          <button type="button" onClick={() => q.toggle(q.index)} aria-label={q.playing ? "Pause" : "Play"} data-testid="queue-toggle" className="h-11 w-11 rounded-full bg-[#e9c46a] text-[#120a0c] grid place-items-center">{q.playing ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}</button>
          <button type="button" onClick={q.next} aria-label="Next" data-testid="queue-next" className="p-2 text-[#f3e9d2]/70 hover:text-[#e9c46a]"><SkipForward size={18} /></button>
        </div>
      </div>
      <div className="h-0.5 bg-[#f3e9d2]/10"><div className="h-full bg-[#e9c46a] transition-[width]" style={{ width: song.duration_sec ? `${Math.min(100, (q.time / song.duration_sec) * 100)}%` : 0 }} /></div>
    </div>
  );
}
